<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import { useBrxmData } from '../../composables/useBrxmData'
import { ECardPhotoSizeDesktop, ECardSizeDesktop, ECardSizeMobile, ECardsListLayout } from './ts/types'
import CardLink from './parts/CardLink.vue'
import CardPhoto from './parts/CardPhoto.vue'
import CardIcon from './parts/CardIcon.vue'
import CardBasic from './parts/CardBasic.vue'
import { useVisibilityTracking } from '@/cms-ui/composables/useAnalytics'
import useBreakpoint from '@/ui/composables/useBreakpoint'
import { EMobileSlidesVariants } from '@/ui/plushComponents/VCarousel/types'
import type { IBrxmProps } from '@/cms-ui/ts/interfaces/IBrxmProps'
import VCarousel from '@/ui/plushComponents/VCarousel/VCarousel.vue'

const props = defineProps<IBrxmProps>()
const data = useBrxmData(props, 'CARDS_LIST')
const cardsListRef = useTemplateRef('cardsListRef')
const { isMobile, isTablet, isDesktop } = useBreakpoint()
const { variant, darkmode, desktopAlignment } = data.value
const { type, desktopImageOff, mobileSize, mobileImageOff, desktopSize, cards, mobileLayout, desktopLayout } = variant

useVisibilityTracking(cardsListRef, 'CardsList', 'CardsList')

const mobileCardVariants = {
    [ECardSizeMobile.Small]: EMobileSlidesVariants.TWO_CARDS,
    [ECardSizeMobile.Large]: EMobileSlidesVariants.ONE_BIG_CARD,
}

const tabletCardVariants = {
    [ECardSizeMobile.Small]: EMobileSlidesVariants.THREE_CARDS,
    [ECardSizeMobile.Large]: EMobileSlidesVariants.TWO_CARDS,
}

const tabletCardIconVariants = {
    [ECardSizeMobile.Small]: EMobileSlidesVariants.FOUR_CARDS,
    [ECardSizeMobile.Large]: EMobileSlidesVariants.TWO_CARDS,
}

const desktopCardsPerView = {
    [ECardPhotoSizeDesktop.Small]: 4,
    [ECardPhotoSizeDesktop.Large]: 3,
    [ECardPhotoSizeDesktop.XLarge]: 2,
    [ECardPhotoSizeDesktop.XXLarge]: 1,
    [ECardPhotoSizeDesktop.Fluid]: '',
}

const isCarousel = computed(() => {
    if (isMobile.value || isTablet.value) {
        return mobileLayout === ECardsListLayout.Carousel
    } else if (isDesktop.value) {
        return desktopLayout === ECardsListLayout.Carousel
    }
    return false
})

function getContainerClasses(): string {
    const classes: string[] = []
    if (mobileLayout === ECardsListLayout.Grid) {
        classes.push('max-lg:flex-wrap', 'max-md:mx-0', 'max-lg:gap-base', 'md:max-lg:mx-0')

        if (mobileSize === ECardSizeMobile.Small) {
            classes.push('max-md:gap-xs')
        }
        if (mobileSize === ECardSizeMobile.Large) {
            classes.push('max-md:gap-base')
        }
    }
    if (desktopLayout === ECardsListLayout.Grid && desktopSize !== ECardSizeDesktop.Fluid) {
        classes.push('lg:flex-wrap', 'lg:gap-md', 'lg:mx-0')
    }
    if (desktopSize === ECardSizeDesktop.Fluid) {
        classes.push('lg:mx-0 lg:gap-md')
    }
    if (desktopAlignment === 'CENTER' && desktopLayout === ECardsListLayout.Grid) {
        classes.push('lg:justify-center')
    }
    return [...new Set(classes)].join(' ')
}
</script>

<template>
    <section
        ref="cardsListRef"
        class="space-standard overflow-hidden"
    >
        <div class="container">
            <div
                v-if="type === 'LINK'"
                class="gap-xs md:gap-base lg:gap-md flex flex-wrap"
                :class="{ 'lg:justify-center': desktopAlignment === 'CENTER' }"
            >
                <CardLink
                    v-for="(card, index) in cards"
                    :key="`card-link-${index}`"
                    :dark="darkmode"
                    :action="card.action"
                    :title="card.title"
                    :image="card.image"
                    :mobile-size="mobileSize"
                    :desktop-size="desktopSize"
                    :mobile-image-off="mobileImageOff"
                    :desktop-image-off="desktopImageOff"
                    :data-test="`card-link-${index}`"
                    :page="page"
                />
            </div>

            <VCarousel
                v-if="type === 'PHOTO'"
                :dark="darkmode"
                :mobile-slides-variant="mobileCardVariants[mobileSize as ECardSizeMobile]"
                :tablet-slides-variant="tabletCardVariants[mobileSize as ECardSizeMobile]"
                :desktop-slides-per-view="desktopCardsPerView[desktopSize as ECardPhotoSizeDesktop]"
                :disable-mobile="mobileLayout === ECardsListLayout.Grid"
                :disable-tablet="mobileLayout === ECardsListLayout.Grid"
                :disable-desktop="desktopLayout === ECardsListLayout.Grid || desktopSize === ECardSizeDesktop.Fluid"
                data-test="cards-list-photo"
                desktop-width="grid"
                carousel-aria-label="cards-list-photo"
                :custom-slides-container-classes="getContainerClasses()"
            >
                <CardPhoto
                    v-for="(card, index) in cards"
                    :key="`card-photo-${index}`"
                    :dark="darkmode"
                    :action="card.action"
                    :title="card.title"
                    :seo-heading-size="card?.seoHeadingSize?.value"
                    :image="card.image"
                    :target="card?.action?.openInNewTab"
                    :mobile-size="mobileSize"
                    :desktop-size="desktopSize"
                    :mobile-layout="mobileLayout"
                    :desktop-layout="desktopLayout"
                    :data-test="`cards-list-photo-${index}`"
                    :page="page"
                    :is-carousel="isCarousel"
                />
                <template #bottom>
                    <VCarouselScrollbar v-if="isCarousel" />
                </template>
            </VCarousel>

            <VCarousel
                v-if="type === 'ICON'"
                :dark="darkmode"
                :mobile-slides-variant="mobileCardVariants[mobileSize as ECardSizeMobile]"
                :tablet-slides-variant="tabletCardIconVariants[mobileSize as ECardSizeMobile]"
                :desktop-slides-per-view="desktopCardsPerView[desktopSize as ECardPhotoSizeDesktop]"
                :disable-mobile="mobileLayout === ECardsListLayout.Grid"
                :disable-tablet="mobileLayout === ECardsListLayout.Grid"
                :disable-desktop="desktopLayout === ECardsListLayout.Grid || desktopSize === ECardSizeDesktop.Fluid"
                data-test="cards-list-icon"
                desktop-width="grid"
                carousel-aria-label="cards-list-icon"
                :custom-slides-container-classes="getContainerClasses()"
            >
                <CardIcon
                    v-for="(card, index) in cards"
                    :key="`card-icon-${index}`"
                    :dark="darkmode"
                    :action="card.action"
                    :title="card.title"
                    :seo-heading-size="card?.seoHeadingSize?.value"
                    :paragraph="card.paragraph.value"
                    :image="card.image"
                    :target="card.action?.openInNewTab"
                    :mobile-size="mobileSize"
                    :desktop-size="desktopSize"
                    :mobile-layout="mobileLayout"
                    :desktop-layout="desktopLayout"
                    :data-test="`card-photo-${index}`"
                    :page="page"
                    :is-carousel="isCarousel"
                />
                <template #bottom>
                    <VCarouselScrollbar v-if="isCarousel" />
                </template>
            </VCarousel>

            <VCarousel
                v-if="type === 'BASIC'"
                :dark="darkmode"
                :mobile-slides-variant="mobileCardVariants[mobileSize as ECardSizeMobile]"
                :tablet-slides-variant="tabletCardIconVariants[mobileSize as ECardSizeMobile]"
                :desktop-slides-per-view="desktopCardsPerView[desktopSize as ECardPhotoSizeDesktop]"
                :disable-mobile="mobileLayout === ECardsListLayout.Grid"
                :disable-tablet="mobileLayout === ECardsListLayout.Grid"
                :disable-desktop="desktopLayout === ECardsListLayout.Grid || desktopSize === ECardSizeDesktop.Fluid"
                data-test="cards-list-basic"
                desktop-width="grid"
                carousel-aria-label="cards-list-basic"
                :custom-slides-container-classes="getContainerClasses()"
            >
                <CardBasic
                    v-for="(card, index) in cards"
                    :key="`card-basic-${index}`"
                    :dark="darkmode"
                    :card-dark="card.darkmode"
                    :action="card.action"
                    :title="card.title"
                    :seo-heading-size="card?.seoHeadingSize?.value"
                    :background="card.background"
                    :paragraph="card.paragraph.value"
                    :image="card.image"
                    :button="card.button"
                    :link="card.link"
                    :target="card.action?.openInNewTab"
                    :button-alignment="card.buttonAlignment"
                    :mobile-size="mobileSize"
                    :desktop-size="desktopSize"
                    :mobile-layout="mobileLayout"
                    :desktop-layout="desktopLayout"
                    :data-test="`card-basic-${index}`"
                    :page="page"
                    :is-carousel="isCarousel"
                />
                <template #bottom>
                    <VCarouselScrollbar v-if="isCarousel" />
                </template>
            </VCarousel>
        </div>
    </section>
</template>
